import { useEffect, useState } from 'react';

import { ALL_PROFILES, PROFILE_LABELS } from './ProfilesBadgeList';
import { useAccount } from '../../contexts/AccountContext';
import { useToast } from '../../contexts/ToastContext';
import { useCreateUser } from '../../services/api/hooks/useCreateUser';
import { useScopeContracts } from '../../services/api/hooks/useScopeContracts';
import { useScopeEntities } from '../../services/api/hooks/useScopeEntities';
import { useUpdateUser } from '../../services/api/hooks/useUpdateUser';
import type {
  CreateUserInput,
  ExtranetUserListItem,
  UpdateUserInput,
  UserProfile,
} from '../../types/extranet-api.types';
import { IconX } from '../incidents/icons';

interface UserFormModalProps {
  mode: 'create' | 'edit';
  user?: ExtranetUserListItem;
  onClose: () => void;
  onSuccess: () => void;
}

export const UserFormModal = ({ mode, user, onClose, onSuccess }: UserFormModalProps) => {
  const { me } = useAccount();
  const toast = useToast();
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [profiles, setProfiles] = useState<UserProfile[]>(user?.profiles ?? []);
  const [entityIds, setEntityIds] = useState<string[]>(user?.scope.entityIds ?? []);
  const [contractIds, setContractIds] = useState<string[]>(user?.scope.contractIds ?? []);
  const [touched, setTouched] = useState(false);

  const entitiesQuery = useScopeEntities();
  const contractsQuery = useScopeContracts({ entityIds });

  // Restriction §4.3 brief : le périmètre attribué ne peut pas dépasser celui de l'admin courant.
  const allowedEntityIds = new Set(me.scope.entityIds);
  const allowedContractIds = new Set(me.scope.contractIds);

  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const isPending = createUser.isPending || updateUser.isPending;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // Cascade : retirer les contracts qui ne sont plus dispo
  useEffect(() => {
    if (!contractsQuery.data) return;
    const available = new Set(contractsQuery.data.contracts.map(c => c.id));
    const filtered = contractIds.filter(id => available.has(id));
    if (filtered.length !== contractIds.length) setContractIds(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractsQuery.data]);

  const entities = (entitiesQuery.data?.entities ?? []).filter(e => allowedEntityIds.has(e.id));
  const contracts = (contractsQuery.data?.contracts ?? []).filter(c => allowedContractIds.has(c.id));

  const emailError =
    email.length > 0 && email.toLowerCase().endsWith('@allocab.com')
      ? 'Adresse email hors domaine client'
      : email.length > 0 && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)
        ? 'Email invalide'
        : null;

  const canSubmit =
    firstName.length > 0 && lastName.length > 0 && email.length > 0 && !emailError && profiles.length > 0 && !isPending;

  const error = (createUser.error ?? updateUser.error) as Error | undefined;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;

    const payload = {
      firstName,
      lastName,
      email,
      profiles,
      scope: { entityIds, contractIds },
    };

    if (mode === 'create') {
      createUser.mutate(payload as CreateUserInput, {
        onSuccess: created => {
          toast.success('Utilisateur créé', `${created.firstName} ${created.lastName} a été ajouté au compte.`);
          onSuccess();
          onClose();
        },
        onError: err => {
          toast.error('Échec de la création', (err as Error).message);
        },
      });
    } else if (user) {
      updateUser.mutate(
        { id: user.id, input: payload as UpdateUserInput },
        {
          onSuccess: () => {
            toast.success('Modifications enregistrées', `${firstName} ${lastName} mis à jour.`);
            onSuccess();
            onClose();
          },
          onError: err => {
            toast.error('Échec de la mise à jour', (err as Error).message);
          },
        },
      );
    }
  };

  const toggleProfile = (p: UserProfile) => {
    setProfiles(prev => (prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]));
  };

  const toggleEntity = (id: string) => {
    setEntityIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const toggleContract = (id: string) => {
    setContractIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[8vh] px-4">
      <div
        className="absolute inset-0 bg-black/30 drawer-backdrop-in"
        style={{ backdropFilter: 'blur(2px)' }}
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-form-title"
        className="relative w-full max-w-[560px] bg-white border border-zinc-200 rounded-md shadow-md drawer-panel-in flex flex-col max-h-[84vh]"
      >
        <header className="sticky top-0 bg-white px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
          <h2 id="user-form-title" className="text-base font-semibold text-zinc-900">
            {mode === 'create' ? 'Nouvel utilisateur' : "Modifier l'utilisateur"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
          >
            <IconX width={16} height={16} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Prénom"
              value={firstName}
              onChange={setFirstName}
              error={touched && !firstName ? 'Requis' : null}
            />
            <Field label="Nom" value={lastName} onChange={setLastName} error={touched && !lastName ? 'Requis' : null} />
          </div>

          <Field
            label="Email"
            value={email}
            onChange={setEmail}
            type="email"
            placeholder="prenom.nom@sncf-demo.fr"
            error={emailError}
          />

          {/* Profils */}
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-zinc-700">
              Profil(s) <span className="text-zinc-400">(cumul possible)</span>
            </span>
            <div className="grid grid-cols-3 gap-1.5">
              {ALL_PROFILES.map(p => {
                const active = profiles.includes(p);
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => toggleProfile(p)}
                    className={[
                      'h-8 px-2.5 text-xs rounded-md border transition-colors duration-100 text-left',
                      active
                        ? 'bg-zinc-900 text-white border-zinc-900'
                        : 'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50',
                    ].join(' ')}
                  >
                    {PROFILE_LABELS[p]}
                  </button>
                );
              })}
            </div>
            {touched && profiles.length === 0 ? (
              <p className="text-xs text-red-600">Au moins un profil requis.</p>
            ) : null}
          </div>

          {/* Périmètre — Entités */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-zinc-700">
              Périmètre — Entités <span className="text-zinc-400">({entityIds.length})</span>
            </label>
            <div className="border border-zinc-200 rounded-md max-h-32 overflow-y-auto p-1.5">
              {entities.length === 0 ? (
                <p className="text-xs text-zinc-500 px-2 py-2">Aucune entité disponible.</p>
              ) : (
                entities.map(ent => {
                  const checked = entityIds.includes(ent.id);
                  return (
                    <button
                      key={ent.id}
                      type="button"
                      onClick={() => toggleEntity(ent.id)}
                      className="w-full px-2 py-1.5 flex items-center gap-2 rounded text-sm text-zinc-700 hover:bg-zinc-50 transition-colors text-left"
                    >
                      <span
                        className={[
                          'h-3.5 w-3.5 rounded-sm border inline-flex items-center justify-center shrink-0',
                          checked ? 'bg-zinc-900 border-zinc-900' : 'border-zinc-300 bg-white',
                        ].join(' ')}
                      >
                        {checked ? <span className="text-[10px] text-white">✓</span> : null}
                      </span>
                      <span className="truncate">{ent.name}</span>
                      <span className="ml-auto text-xs text-zinc-500 font-mono tabular-nums shrink-0">{ent.id}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Périmètre — Contrats */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-zinc-700">
              Périmètre — Contrats <span className="text-zinc-400">({contractIds.length})</span>
            </label>
            <div className="border border-zinc-200 rounded-md max-h-44 overflow-y-auto p-1.5">
              {contractsQuery.isLoading ? (
                <p className="text-xs text-zinc-500 px-2 py-2">Chargement…</p>
              ) : contracts.length === 0 ? (
                <p className="text-xs text-zinc-500 px-2 py-2">
                  {entityIds.length > 0
                    ? 'Aucun contrat sur ces entités.'
                    : 'Sélectionnez une entité pour voir les contrats.'}
                </p>
              ) : (
                contracts.map(c => {
                  const checked = contractIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleContract(c.id)}
                      className="w-full px-2 py-1.5 flex items-center gap-2 rounded text-sm text-zinc-700 hover:bg-zinc-50 transition-colors text-left"
                    >
                      <span
                        className={[
                          'h-3.5 w-3.5 rounded-sm border inline-flex items-center justify-center shrink-0',
                          checked ? 'bg-zinc-900 border-zinc-900' : 'border-zinc-300 bg-white',
                        ].join(' ')}
                      >
                        {checked ? <span className="text-[10px] text-white">✓</span> : null}
                      </span>
                      <span className="truncate text-xs">{c.label}</span>
                      <span className="ml-auto text-xs text-zinc-500 font-mono tabular-nums shrink-0">{c.id}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {error ? (
            <p className="text-xs px-3 py-2 bg-red-50 border border-red-100 text-red-700 rounded-md">
              {error.message || "Échec de l'opération. Réessayer."}
            </p>
          ) : null}
        </form>

        <footer className="px-6 py-3 border-t border-zinc-100 flex items-center justify-end gap-2 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-3 text-sm text-zinc-700 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="h-9 px-4 text-sm font-medium bg-zinc-900 text-white rounded-md hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isPending ? 'Enregistrement…' : mode === 'create' ? 'Créer' : 'Enregistrer'}
          </button>
        </footer>
      </div>
    </div>
  );
};

const Field = ({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  error?: string | null;
}) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-medium text-zinc-700">{label}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={[
        'h-10 w-full px-3 text-sm bg-white border rounded-md text-zinc-900 placeholder:text-zinc-400 transition-colors',
        'focus:outline-none focus:ring-2',
        error
          ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
          : 'border-zinc-300 focus:border-zinc-400 focus:ring-zinc-900/10',
      ].join(' ')}
    />
    {error ? <p className="text-xs text-red-600">{error}</p> : null}
  </div>
);
